import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { Hub, HubCapsule } from '@aws-amplify/core';
import { Authenticator } from 'aws-amplify-react';
import { isMobile } from 'react-device-detect';
import { Provider } from 'react-redux';
import { sleep, store } from 'core';

import { Preloader } from 'components/preloader';

import { Modules } from 'core/types';

import { getGoogleFonts } from 'core/actions';
import { config } from 'core/services/Auth';
import { checkBackendHealth } from 'core/services/health';
import { logout } from 'core/utils/logout';
import { getUserInfo } from 'modules/auth/actions';
import {
  ConfirmationWasSent,
  ConfirmSignIn,
  ForgotPassword,
  MobilePreview,
  SessionRecovery,
  SignIn,
  SignUp,
} from 'modules/auth/containers';

Auth.configure(config);

const BaseLayout = lazy(() => import('components/layout/base-layout'));
const Maintenance = lazy(() => import('modules/maintenance/maintenance'));

type SessionState = 'checking' | 'authenticated' | 'signedOut' | 'recoverableError';

const SESSION_RETRY_DELAYS_MS = [500, 1500];

const handleUserSubscription = ({ payload }: HubCapsule) => {
  if (payload.event === 'updateUserAttributes') {
    store.dispatch(getUserInfo());
  }
};

const AppComponent = ({ routes }: Partial<Modules>) => {
  useEffect(() => {
    store.dispatch(getGoogleFonts());
  }, []);

  useEffect(() => {
    Hub.listen('auth', handleUserSubscription);
    return () => {
      Hub.remove('auth', handleUserSubscription);
    };
  }, []);

  return (
    <Provider store={store}>
      <Suspense fallback={<Preloader />}>
        <BaseLayout routes={routes} />
      </Suspense>
    </Provider>
  );
};

export const App = (props: Modules) => {
  const urlParams = new URLSearchParams(window.location.search);
  const webViewType = urlParams.get('webview');
  const authPage = urlParams.get('authPage');
  const skipAppPlaceholder = urlParams.get('skipapp');

  const isSkipPreview = webViewType === 'mobile' && authPage === 'signin';
  const initialAuthState =
    isMobile && !isSkipPreview && !skipAppPlaceholder ? 'mobilePreview' : 'signIn';

  const [sessionState, setSessionState] = useState<SessionState>('checking');
  const [isSessionRetrying, setIsSessionRetrying] = useState(false);
  const [maintenanceState, setMaintenanceState] = useState<'checking' | 'healthy' | 'maintenance'>(
    'checking'
  );

  const clearTerminalSession = useCallback(async () => {
    await logout({ redirect: false });
    setSessionState('signedOut');
  }, []);

  const restoreSession = useCallback(
    async (showRecoveryProgress = false) => {
      if (showRecoveryProgress) {
        setIsSessionRetrying(true);
      } else {
        setSessionState('checking');
      }

      for (let attempt = 0; attempt <= SESSION_RETRY_DELAYS_MS.length; attempt += 1) {
        if (attempt > 0) {
          await sleep(SESSION_RETRY_DELAYS_MS[attempt - 1]);
        }

        const resultAction = await store.dispatch(getUserInfo());

        if (getUserInfo.fulfilled.match(resultAction)) {
          setSessionState('authenticated');
          setIsSessionRetrying(false);
          return;
        }

        if (resultAction.payload?.kind === 'terminal') {
          await clearTerminalSession();
          setIsSessionRetrying(false);
          return;
        }
      }

      setSessionState('recoverableError');
      setIsSessionRetrying(false);
    },
    [clearTerminalSession]
  );

  const handleAuthStateChange = useCallback(
    (state: string) => {
      if (state === 'signedIn') {
        restoreSession();
      }
    },
    [restoreSession]
  );

  const handleSignInAgain = useCallback(async () => {
    setIsSessionRetrying(true);
    await clearTerminalSession();
    setIsSessionRetrying(false);
  }, [clearTerminalSession]);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  useEffect(() => {
    const handleMaintenanceMode = async () => {
      const isHealthy = await checkBackendHealth();

      setMaintenanceState(isHealthy ? 'healthy' : 'maintenance');
    };

    handleMaintenanceMode();
  }, []);

  if (maintenanceState === 'checking') {
    return <Preloader />;
  }

  if (maintenanceState === 'maintenance') {
    return (
      <Suspense fallback={<Preloader />}>
        <Maintenance />
      </Suspense>
    );
  }

  if (sessionState === 'checking') {
    return <Preloader />;
  }

  if (sessionState === 'authenticated') {
    return <AppComponent {...props} />;
  }

  if (sessionState === 'recoverableError') {
    return (
      <SessionRecovery
        processing={isSessionRetrying}
        onRetry={() => restoreSession(true)}
        onSignIn={handleSignInAgain}
      />
    );
  }

  return (
    <Authenticator
      hideDefault={true}
      authState={initialAuthState}
      onStateChange={handleAuthStateChange}>
      <MobilePreview key="mobilePreview" />
      <SignIn key="signIn" />
      <SignUp key="signUp" />
      <ConfirmationWasSent key="confirmSignUp" />
      <ForgotPassword key="forgotPassword" />
      <ConfirmSignIn key="confirmSignIn" />
    </Authenticator>
  );
};
